import { ChangeEvent, useMemo, useRef } from 'react';
import { useAppSelector } from '@shared/store';
import { usePlayerContext } from 'application/providers/PlayerProvider';
import { ACCEPT_AUDIO_FILETYPES } from 'application/pages/constants';
import { VIDEO_MEDIA_ACCEPT } from '@shared/config/media';
import CommonUtils from 'application/utils/CommonUtils';
import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';
import { AdCampaignTypeEnum } from '@entities/media';
import { MediaUploaderItemValue } from '@zvuk-b2b/react-uikit/ui/MediaUploader';
import { TIME_PATTERN } from '@zvuk-b2b/react-uikit/ui/TimeInput';
import { selectCampaignType } from '../../../slices/createAdCampaign/selectors';
import { toMediaUploaderItem } from '../../AdCampaignMedia/utils';
import { filterAcceptedVideoFiles } from '../../AdCampaignMedia/videoPromoFiles';
import { TEXTS, UPLOAD_BUTTON_TEXTS } from './texts';
import { UseAdCampaignMediaViewModelProps } from './types';
import { areVideoPromosValid } from '../../AdCampaignMedia/videoPromoDraft';

export const useAdCampaignMediaViewModel = ({
  mediaType = AdCampaignMediaTypeEnum.Audio,
  promoState,
  timeById,
  durationById,
  onTimeChange,
  onTimeRemove,
}: UseAdCampaignMediaViewModelProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const campaignType = useAppSelector(selectCampaignType);
  const player = usePlayerContext();
  const { data, upload, remove, retry, reorder } = promoState;

  const isExactTime = campaignType === AdCampaignTypeEnum.ExactTime;
  const isInstant = campaignType === AdCampaignTypeEnum.Instant;
  const isDraggable = !isInstant;
  const accept = mediaType === AdCampaignMediaTypeEnum.Audio ? ACCEPT_AUDIO_FILETYPES : VIDEO_MEDIA_ACCEPT;
  const acceptValue = Object.values(accept).flat().join(',');
  const uploadButtonText = UPLOAD_BUTTON_TEXTS[mediaType];
  const uploadDescription = TEXTS.MEDIA_UPLOAD_DESC(CommonUtils.convertObjectValuesToString(accept));
  const items = isDraggable ? data.rawStatuses : data.statuses;
  const mediaUploadItems = useMemo(() => items.map(toMediaUploaderItem), [items]);

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleFilesSelected = (files: File[]) => {
    const acceptedFiles = mediaType === AdCampaignMediaTypeEnum.Video ? filterAcceptedVideoFiles(files) : files;

    if (acceptedFiles.length > 0) {
      upload(acceptedFiles, mediaType);
    }
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (files.length > 0) {
      handleFilesSelected(files);
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemoveClick = (id: string) => {
    if (player.fileUuid === id) {
      player.stop?.();
    }

    remove(id);
    onTimeRemove(id);
  };

  const handleRetryClick = (id: string) => {
    retry(id, mediaType);
  };

  const handleRemoveMedia = (value: MediaUploaderItemValue) => {
    handleRemoveClick(value.id);
  };

  const handleRetryMedia = (value: MediaUploaderItemValue) => {
    handleRetryClick(value.id);
  };

  const handlePlayClick = (id: string, filename: string, url: string) => {
    player.play?.({ fileName: filename, fileUuid: id, fileUrl: url });
  };

  const handleTimeBlur = (id: string) => {
    const value = timeById[id];
    if (value !== undefined && !TIME_PATTERN.test(value)) {
      onTimeChange(id, '');
    }
  };

  const isUploadDisabled = isInstant && items.length > 0;

  const isAudioNextDisabled =
    items.length === 0 ||
    items.some((item) => item.isLoading || item.error || !item.promoId) ||
    (isExactTime && items.some((item) => item.id && !TIME_PATTERN.test(timeById[item.id] ?? '')));

  const isNextDisabled =
    mediaType === AdCampaignMediaTypeEnum.Video
      ? !areVideoPromosValid(items, isExactTime, timeById, durationById)
      : isAudioNextDisabled;

  return {
    inputRef,
    isExactTime,
    isDraggable,
    acceptValue,
    uploadButtonText,
    uploadDescription,
    items,
    player,
    mediaUploadItems,
    isUploadDisabled,
    isNextDisabled,
    handleUploadClick,
    handleFilesChange,
    handleFilesSelected,
    handleRemoveClick,
    handleRetryClick,
    handleRemoveMedia,
    handleRetryMedia,
    handlePlayClick,
    handleTimeBlur,
    reorder,
  };
};
